import { create } from "zustand";
import type { Note, NoteTag, SortField, SortDirection, Comment } from "../types";
import {
  listAllNotes as apiListNotes,
  getNote as apiGetNote,
  createNote as apiCreateNote,
  updateNote as apiUpdateNote,
  deleteNote as apiDeleteNote,
  listNoteComments as apiListNoteComments,
  createNoteComment as apiCreateNoteComment,
  updateNoteComment as apiUpdateNoteComment,
  deleteNoteComment as apiDeleteNoteComment,
  shareNote as apiShareNote,
  unshareNote as apiUnshareNote,
  leaveSharedNote as apiLeaveSharedNote,
  listSharedNotes as apiListSharedNotes,
  useUserStore,
} from "@slumbr/core";
import type {
  NoteCommentResponse,
  NoteResponse,
  NoteShareRole,
} from "@slumbr/core";
import { useProfileSwitcherStore } from "@/stores/profile-switcher-store";
import { generateUUID } from "@/utils/uuid";

/**
 * localStorage key prefixes. Keys are scoped by active profile id so
 * switching personas — or signing in as a different account on this device —
 * never surfaces another user's notes. Storage layout:
 *   slumbr:notes:{profileId}
 *   slumbr:sharedNotes:{profileId}
 *   slumbr:comments:{profileId}
 * The legacy unscoped keys (slumbr:notes, etc.) are migrated to the active
 * profile on first hydration, then deleted.
 */
const STORAGE_PREFIXES = {
  COMMENTS: "slumbr:comments",
  NOTES: "slumbr:notes",
  SHARED_NOTES: "slumbr:sharedNotes",
} as const;

function scopedKey(prefix: string, profileId: string | null): string | null {
  if (!profileId) return null;
  return `${prefix}:${profileId}`;
}

function currentProfileId(): string | null {
  // Active persona ("profile switcher") wins. Falls back to the authenticated
  // Cognito userId so the first-load case (no profile selected yet) still
  // scopes correctly.
  if (typeof window === "undefined") return null;
  const persona =
    useProfileSwitcherStore.getState().activeProfileId ?? null;
  if (persona) return persona;
  return useUserStore.getState().user?.id ?? null;
}

function migrateLegacyKey(legacy: string, profileId: string | null): void {
  if (typeof window === "undefined" || !profileId) return;
  try {
    const stored = localStorage.getItem(legacy);
    if (!stored) return;
    const target = `${legacy}:${profileId}`;
    // Don't clobber an existing scoped entry — the migration ran already.
    if (localStorage.getItem(target) === null) {
      localStorage.setItem(target, stored);
    }
    localStorage.removeItem(legacy);
  } catch {
    // localStorage disabled / quota — best-effort
  }
}

function loadCommentsFromStorage(profileId: string | null): Comment[] {
  try {
    if (typeof window === "undefined") return [];
    const key = scopedKey(STORAGE_PREFIXES.COMMENTS, profileId);
    if (!key) return [];
    const stored = localStorage.getItem(key);
    if (!stored) return [];
    return JSON.parse(stored) as Comment[];
  } catch (err) {
    console.error("Failed to load comments from localStorage:", err);
    return [];
  }
}

function saveCommentsToStorage(comments: Comment[], profileId: string | null): void {
  try {
    if (typeof window === "undefined") return;
    const key = scopedKey(STORAGE_PREFIXES.COMMENTS, profileId);
    if (!key) return;
    localStorage.setItem(key, JSON.stringify(comments));
  } catch (err) {
    console.error("Failed to save comments to localStorage:", err);
  }
}

/**
 * Generic load helper used to hydrate the notes lists synchronously on
 * store init so navigating to /notes/:id doesn't show "Note not found"
 * while the network fetch runs. Returns the fallback if no profile is
 * active yet (signed out / first boot).
 */
function loadFromStorage<T>(prefix: string, profileId: string | null, fallback: T): T {
  try {
    if (typeof window === "undefined") return fallback;
    const key = scopedKey(prefix, profileId);
    if (!key) return fallback;
    const stored = localStorage.getItem(key);
    if (!stored) return fallback;
    return JSON.parse(stored) as T;
  } catch {
    return fallback;
  }
}

let notesPersistTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleNotesPersist(notes: Note[], sharedNotes: Note[]): void {
  if (typeof window === "undefined") return;
  if (notesPersistTimer) clearTimeout(notesPersistTimer);
  notesPersistTimer = setTimeout(() => {
    const profileId = currentProfileId();
    if (!profileId) return;
    try {
      const notesKey = scopedKey(STORAGE_PREFIXES.NOTES, profileId);
      const sharedKey = scopedKey(STORAGE_PREFIXES.SHARED_NOTES, profileId);
      if (notesKey) localStorage.setItem(notesKey, JSON.stringify(notes));
      if (sharedKey) localStorage.setItem(sharedKey, JSON.stringify(sharedNotes));
    } catch {
      // Quota exceeded or disabled — silently drop.
    }
  }, 300);
}

function clearScopedStorage(profileId: string | null): void {
  if (typeof window === "undefined" || !profileId) return;
  try {
    for (const prefix of Object.values(STORAGE_PREFIXES)) {
      const key = scopedKey(prefix, profileId);
      if (key) localStorage.removeItem(key);
    }
  } catch {
    // Storage disabled — best-effort
  }
}

/**
 * Recursively collect all descendant IDs of a given note.
 * Used to prevent cycles when reparenting (moving) a note.
 */
export function getDescendantIds(noteId: string, notes: Note[]): string[] {
  const directChildren = notes.filter((n) => n.parentId === noteId);
  const ids: string[] = [];
  for (const child of directChildren) {
    ids.push(child.id);
    ids.push(...getDescendantIds(child.id, notes));
  }
  return ids;
}

function getSiblingOrder(notes: Note[], parentId: string | null) {
  return notes
    .filter((note) => (note.parentId ?? null) === parentId && !note.isDeleted)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id.localeCompare(b.id));
}

function normalizeSiblingOrders(notes: Note[], parentId: string | null) {
  const siblingIds = getSiblingOrder(notes, parentId).map((note) => note.id);

  return notes.map((note) => {
    if ((note.parentId ?? null) !== parentId || note.isDeleted) {
      return note;
    }

    const nextOrder = siblingIds.indexOf(note.id);
    return nextOrder === -1 || note.order === nextOrder
      ? note
      : { ...note, order: nextOrder };
  });
}

function normalizeAllOrders(notes: Note[]) {
  const parentIds = new Set<string | null>();

  for (const note of notes) {
    if (!note.isDeleted) {
      parentIds.add(note.parentId ?? null);
    }
  }

  let nextNotes = notes;
  for (const parentId of parentIds) {
    nextNotes = normalizeSiblingOrders(nextNotes, parentId);
  }

  return nextNotes;
}

/**
 * When dev auth bypass is enabled, skip all remote API calls and work
 * entirely with in-memory state so the app can be tested locally without
 * a running serverless backend.
 */
const DEV_AUTH_BYPASS =
  import.meta.env.VITE_DEV_AUTH_BYPASS === "true";

/**
 * Map a NoteResponse from the API into a local Note.
 * `createdBy` is scoped by Cognito server-side and not returned to the UI.
 */
function mapNoteResponse(item: NoteResponse): Note {
  return {
    id: item.id,
    title: item.title,
    content: item.content as Note["content"],
    parentId: item.parentId,
    tags: item.tags,
    isFavorite: item.isFavorite,
    isDeleted: item.isDeleted,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    order: item.order ?? 0,
    createdBy: item.ownerId ?? "",
    icon: item.icon,
    coverImage: item.coverImage,
    fullWidth: item.fullWidth,
    isPage: item.isPage,
    color: item.color,
    pageOrder: item.pageOrder,
    ownerId: item.ownerId,
    sharedWith: item.sharedWith,
    myRole: item.myRole,
    yjsState: item.yjsState,
  };
}

function mapCommentResponse(item: NoteCommentResponse): Comment {
  return {
    id: item.id,
    noteId: item.noteId,
    text: item.text,
    author: item.author,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    isResolved: item.isResolved,
    threadId: item.threadId,
    highlightStart: item.highlightStart,
    highlightEnd: item.highlightEnd,
  };
}

interface NotesStore {
  notes: Note[];
  /** Notes shared WITH the current user (separate from owned notes). */
  sharedNotes: Note[];
  activeNoteId: string | null;
  tags: NoteTag[];
  searchQuery: string;
  selectedTags: string[];
  sortField: SortField;
  sortDirection: SortDirection;
  isLoading: boolean;
  error: string | null;
  comments: Comment[];
  activeCommentThreadId: string | null;

  // Actions
  setNotes: (notes: Note[]) => void;
  addNote: (note: Note) => void;
  updateNote: (id: string, updates: Partial<Note>) => void;
  deleteNote: (id: string) => void;
  restoreNote: (id: string) => void;
  permanentlyDeleteNote: (id: string) => void;
  setActiveNote: (id: string | null) => void;
  duplicateNote: (id: string) => string | undefined;
  moveNote: (id: string, newParentId: string | null) => void;
  reorderNote: (id: string, targetParentId: string | null, targetIndex: number) => void;
  toggleFavorite: (id: string) => void;
  setSearchQuery: (query: string) => void;
  setSelectedTags: (tags: string[]) => void;
  setSort: (field: SortField, direction: SortDirection) => void;
  setLoading: (loading: boolean) => void;

  // Comment actions
  addComment: (comment: Comment) => void;
  updateComment: (id: string, updates: Partial<Comment>) => void;
  deleteComment: (id: string) => void;
  getCommentsForNote: (noteId: string) => Comment[];
  getCommentsForThread: (threadId: string) => Comment[];
  setActiveCommentThread: (threadId: string | null) => void;
  resolveComment: (id: string) => void;

  // Async API actions
  fetchNotes: () => Promise<void>;
  fetchSharedNotes: () => Promise<void>;
  /** Refresh one note from the server, replacing the cached entry. The optional
   *  `pageParentId` lets a shared-note recipient fetch a page via the parent
   *  share (the page has no share pointer of its own). */
  refreshNote: (id: string, pageParentId?: string | null) => Promise<void>;
  /** Lazily fetch comments for one note. Replaces only that note's comments
   *  in the store — leaves other notes' cached comments untouched. */
  fetchCommentsForNote: (noteId: string) => Promise<void>;
  createNoteRemote: (data: {
    title?: string;
    parentId?: string | null;
    content?: Note["content"];
  }) => Promise<string>;
  updateNoteRemote: (
    id: string,
    updates: Partial<Note>,
    pageParentId?: string | null
  ) => Promise<void>;
  deleteNoteRemote: (id: string) => Promise<void>;

  // In-note pages
  /** Create a new page under a note. Returns the new page id. */
  createPage: (parentNoteId: string, title?: string) => Promise<string>;
  /** Soft-delete a page and drop it from the parent's pageOrder. */
  deletePage: (pageId: string) => Promise<void>;
  /** Persist a new page ordering for a note. */
  reorderPages: (parentNoteId: string, orderedPageIds: string[]) => void;

  // Sharing
  shareNoteWith: (noteId: string, userId: string, role: NoteShareRole) => Promise<void>;
  unshareNoteWith: (noteId: string, userId: string) => Promise<void>;
  leaveShared: (noteId: string) => Promise<void>;

  // Lifecycle: wipe in-memory + persisted state. Called on profile switch
  // or logout so a different account's notes can never leak through.
  resetForProfile: (nextProfileId: string | null) => void;

  // Computed
  getActiveNote: () => Note | undefined;
  getFilteredNotes: () => Note[];
  getRootNotes: () => Note[];
  getChildNotes: (parentId: string) => Note[];
  /** In-note pages belonging to a note, ordered for the page rail. */
  getPagesForNote: (noteId: string) => Note[];
  getFavoriteNotes: () => Note[];
  getDeletedNotes: () => Note[];
}

// Resolve the active profile once at module load so the initial state is
// scoped correctly. The profile-switcher subscriber below also runs the
// migration on each switch.
const initialProfileId =
  typeof window === "undefined" ? null : currentProfileId();
migrateLegacyKey(STORAGE_PREFIXES.NOTES, initialProfileId);
migrateLegacyKey(STORAGE_PREFIXES.SHARED_NOTES, initialProfileId);
migrateLegacyKey(STORAGE_PREFIXES.COMMENTS, initialProfileId);

export const useNotesStore = create<NotesStore>((set, get) => ({
  // Hydrate from localStorage so the UI is ready before the network fetch
  // resolves. Scoped by profile id so a previous user's notes never appear
  // after a profile switch.
  notes: loadFromStorage<Note[]>(STORAGE_PREFIXES.NOTES, initialProfileId, []),
  sharedNotes: loadFromStorage<Note[]>(
    STORAGE_PREFIXES.SHARED_NOTES,
    initialProfileId,
    []
  ),
  activeNoteId: null,
  tags: [],
  searchQuery: "",
  selectedTags: [],
  sortField: "updatedAt",
  sortDirection: "desc",
  isLoading: false,
  error: null,
  comments: DEV_AUTH_BYPASS ? loadCommentsFromStorage(initialProfileId) : [],
  activeCommentThreadId: null,

  setNotes: (notes) => set({ notes: normalizeAllOrders(notes) }),

  addNote: (note) =>
    set((s) => {
      const siblingCount = s.notes.filter(
        (existing) =>
          (existing.parentId ?? null) === (note.parentId ?? null) && !existing.isDeleted
      ).length;

      return {
        notes: normalizeAllOrders([
          { ...note, order: note.order ?? siblingCount },
          ...s.notes,
        ]),
      };
    }),

  updateNote: (id, updates) =>
    set((s) => {
      const stamp = new Date().toISOString();
      const inOwned = s.notes.some((n) => n.id === id);
      if (inOwned) {
        return {
          notes: normalizeAllOrders(
            s.notes.map((n) =>
              n.id === id ? { ...n, ...updates, updatedAt: stamp } : n
            )
          ),
        };
      }
      // Shared note — apply locally to sharedNotes
      return {
        sharedNotes: s.sharedNotes.map((n) =>
          n.id === id ? { ...n, ...updates, updatedAt: stamp } : n
        ),
      };
    }),

  deleteNote: (id) =>
    set((s) => ({
      notes: normalizeAllOrders(
        s.notes.map((n) =>
          n.id === id ? { ...n, isDeleted: true } : n
        )
      ),
    })),

  restoreNote: (id) => {
    set((s) => ({
      notes: normalizeAllOrders(
        s.notes.map((n) =>
          n.id === id ? { ...n, isDeleted: false } : n
        )
      ),
    }));
    if (!DEV_AUTH_BYPASS) {
      void apiUpdateNote(id, { isDeleted: false }).catch((err) => {
        set({ error: err instanceof Error ? err.message : "Failed to restore note" });
      });
    }
  },

  permanentlyDeleteNote: (id) => {
    set((s) => ({
      notes: normalizeAllOrders(s.notes.filter((n) => n.id !== id)),
    }));
    if (!DEV_AUTH_BYPASS) {
      void apiDeleteNote(id, { hard: true }).catch((err) => {
        set({
          error:
            err instanceof Error ? err.message : "Failed to permanently delete note",
        });
      });
    }
  },

   duplicateNote: (id) => {
      const note = get().notes.find((n) => n.id === id);
      if (!note) return undefined;
      const siblingCount = get().notes.filter(
        (existing) =>
          (existing.parentId ?? null) === (note.parentId ?? null) && !existing.isDeleted
      ).length;
      const newNote: Note = {
        ...note,
        id: generateUUID(),
       title: `${note.title} (Copy)`,
       content: note.content != null ? structuredClone(note.content) : null,
       tags: [...note.tags],
       isFavorite: false,
       isDeleted: false,
       createdAt: new Date().toISOString(),
       updatedAt: new Date().toISOString(),
       order: siblingCount,
      };
      set((s) => ({ notes: normalizeAllOrders([newNote, ...s.notes]) }));
      if (!DEV_AUTH_BYPASS) {
        void apiCreateNote({
          id: newNote.id,
          title: newNote.title,
          content: newNote.content,
          parentId: newNote.parentId,
          tags: newNote.tags,
          order: newNote.order,
          icon: newNote.icon,
          coverImage: newNote.coverImage,
          fullWidth: newNote.fullWidth,
        }).catch((err) => {
          set((s) => ({
            notes: s.notes.filter((entry) => entry.id !== newNote.id),
            error: err instanceof Error ? err.message : "Failed to duplicate note",
          }));
        });
      }
      return newNote.id;
    },

  moveNote: (id, newParentId) => {
    const notes = get().notes;
    const note = notes.find((entry) => entry.id === id);
    if (!note) return;
    if (newParentId && getDescendantIds(id, notes).includes(newParentId)) return;

    const siblingCount = notes.filter(
      (entry) =>
        entry.id !== id &&
        (entry.parentId ?? null) === (newParentId ?? null) &&
        !entry.isDeleted
    ).length;

    const nextNotes = normalizeAllOrders(
      notes.map((entry) =>
        entry.id === id
          ? {
              ...entry,
              parentId: newParentId,
              order: siblingCount,
              updatedAt: new Date().toISOString(),
            }
          : entry
      )
    );

    set({ notes: nextNotes });

    if (!DEV_AUTH_BYPASS) {
      const affectedParents = new Set([note.parentId ?? null, newParentId ?? null]);
      void Promise.all(
        nextNotes
          .filter(
            (entry) =>
              !entry.isDeleted && affectedParents.has(entry.parentId ?? null)
          )
          .map((entry) =>
            apiUpdateNote(entry.id, {
              parentId: entry.parentId ?? null,
              order: entry.order ?? 0,
            })
          )
      ).catch((err) => {
        set({ error: err instanceof Error ? err.message : "Failed to move note" });
      });
    }
  },

  reorderNote: (id, targetParentId, targetIndex) => {
    const prevNotes = JSON.parse(JSON.stringify(get().notes));
    const notes = get().notes;
    const note = notes.find((entry) => entry.id === id);
    if (!note) return;
    if (targetParentId && getDescendantIds(id, notes).includes(targetParentId)) {
      return;
    }

    const siblings = notes
      .filter(
        (entry) =>
          entry.id !== id &&
          (entry.parentId ?? null) === (targetParentId ?? null) &&
          !entry.isDeleted
      )
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id.localeCompare(b.id));

    const nextIndex = Math.max(0, Math.min(targetIndex, siblings.length));
    const reorderedIds = siblings.map((entry) => entry.id);
    reorderedIds.splice(nextIndex, 0, id);

    const baseNotes = notes.map((entry) =>
      entry.id === id
        ? {
            ...entry,
            parentId: targetParentId,
            updatedAt: new Date().toISOString(),
          }
        : entry
    );

    const nextNotes = normalizeAllOrders(
      baseNotes.map((entry) => {
        if ((entry.parentId ?? null) !== (targetParentId ?? null) || entry.isDeleted) {
          return entry;
        }

        const order = reorderedIds.indexOf(entry.id);
        return order === -1 ? entry : { ...entry, order };
      })
    );

    set({ notes: nextNotes });

    if (!DEV_AUTH_BYPASS) {
      void Promise.all(
        nextNotes
          .filter(
            (entry) =>
              !entry.isDeleted &&
              (entry.parentId ?? null) === (targetParentId ?? null)
          )
          .map((entry) =>
            apiUpdateNote(entry.id, {
              parentId: entry.parentId ?? null,
              order: entry.order ?? 0,
            })
          )
      ).catch((err) => {
        set({
          notes: prevNotes,
          error: err instanceof Error ? err.message : "Failed to reorder note",
        });
      });
    }
  },

  setActiveNote: (activeNoteId) => set({ activeNoteId }),

  toggleFavorite: (id) => {
    // Owner's note: write local + remote (canonical isFavorite).
    // Shared note: write local + remote — backend routes the flag to the
    // recipient's NoteShareItem so each user has their own per-user favorite.
    const ownedNote = get().notes.find((entry) => entry.id === id);
    const sharedNote = get().sharedNotes.find((entry) => entry.id === id);
    const note = ownedNote ?? sharedNote;
    if (!note) return;
    const isFavorite = !note.isFavorite;
    const stamp = new Date().toISOString();

    if (ownedNote) {
      set((s) => ({
        notes: normalizeAllOrders(
          s.notes.map((n) =>
            n.id === id ? { ...n, isFavorite, updatedAt: stamp } : n
          )
        ),
      }));
    } else {
      set((s) => ({
        sharedNotes: s.sharedNotes.map((n) =>
          n.id === id ? { ...n, isFavorite, updatedAt: stamp } : n
        ),
      }));
    }

    if (!DEV_AUTH_BYPASS) {
      void apiUpdateNote(id, { isFavorite }).catch((err) => {
        set({ error: err instanceof Error ? err.message : "Failed to update note" });
      });
    }
  },

  setSearchQuery: (searchQuery) => set({ searchQuery }),

  setSelectedTags: (selectedTags) => set({ selectedTags }),

  setSort: (sortField, sortDirection) => set({ sortField, sortDirection }),

  setLoading: (isLoading) => set({ isLoading }),

  // --- Comment actions ---

  addComment: (comment) => {
    set((s) => {
      const newComments = [comment, ...s.comments];
      if (DEV_AUTH_BYPASS) saveCommentsToStorage(newComments, currentProfileId());
      return { comments: newComments };
    });

    if (!DEV_AUTH_BYPASS) {
      void apiCreateNoteComment(comment).catch((err) => {
        set((s) => ({
          comments: s.comments.filter((entry) => entry.id !== comment.id),
          error: err instanceof Error ? err.message : "Failed to save comment",
        }));
      });
    }
  },

  updateComment: (id, updates) => {
    const comment = get().comments.find((entry) => entry.id === id);
    if (!comment) return;

    set((s) => {
      const newComments = s.comments.map((c) =>
        c.id === id
          ? { ...c, ...updates, updatedAt: new Date().toISOString() }
          : c
      );
      if (DEV_AUTH_BYPASS) saveCommentsToStorage(newComments, currentProfileId());
      return { comments: newComments };
    });

    if (!DEV_AUTH_BYPASS) {
      void apiUpdateNoteComment(id, comment.noteId, updates).catch((err) => {
        set({ error: err instanceof Error ? err.message : "Failed to update comment" });
      });
    }
  },

  deleteComment: (id) => {
    const comment = get().comments.find((entry) => entry.id === id);
    if (!comment) return;

    set((s) => {
      const newComments = s.comments.filter((c) => c.id !== id);
      if (DEV_AUTH_BYPASS) saveCommentsToStorage(newComments, currentProfileId());
      return { comments: newComments };
    });

    if (!DEV_AUTH_BYPASS) {
      void apiDeleteNoteComment(id, comment.noteId).catch((err) => {
        set({ error: err instanceof Error ? err.message : "Failed to delete comment" });
      });
    }
  },

  getCommentsForNote: (noteId) => {
    return get().comments.filter((c) => c.noteId === noteId);
  },

  getCommentsForThread: (threadId) => {
    return get().comments.filter((c) => c.threadId === threadId);
  },

  setActiveCommentThread: (threadId) =>
    set({ activeCommentThreadId: threadId }),

  resolveComment: (id) => {
    get().updateComment(id, { isResolved: true });
  },

  // --- Async API actions ---

  fetchNotes: async () => {
    // In dev bypass mode, skip the network call and keep local state
    if (DEV_AUTH_BYPASS) {
      set({ isLoading: false, error: null });
      return;
    }

    const hasCachedNotes = get().notes.length > 0 || get().sharedNotes.length > 0;
    set({ isLoading: !hasCachedNotes, error: null });
    try {
      // Comments are NOT fetched here. The unscoped /api/notes/comments call
      // returns every comment the user has authored or received across every
      // note (up to 500), and this list ran on every focus + visibilitychange
      // + 5min interval. Comments now load lazily per note via
      // `fetchCommentsForNote`, called by NotePage on mount.
      const [response, sharedResponse] = await Promise.all([
        apiListNotes(),
        apiListSharedNotes().catch(() => ({ items: [] as NoteResponse[] })),
      ]);
      const serverNotes = response.items.map(mapNoteResponse);
      const serverShared = sharedResponse.items.map(mapNoteResponse);

      // Last-write-wins merge by updatedAt. A naive replace blows away the
      // user's in-flight edits — they tap a key, the autosave hasn't flushed
      // yet, and the periodic revalidate pulls the now-stale server copy back
      // in, deleting the typed character. By preferring whichever side has
      // the newer updatedAt we keep local edits visible until the next save
      // returns, at which point both sides converge.
      // Collect ids whose server side claims a newer updatedAt — we
      // backfill these with `refreshNote(id)` after the merge so the
      // stripped content/yjsState gets pulled in via get-note. Without
      // this, list refreshes can update only metadata and the body stays
      // stale until the user navigates back to the note.
      const staleOwnedIds: string[] = [];
      const staleSharedIds: string[] = [];
      set((s) => {
        // List endpoints strip `content` and `yjsState` to keep payloads
        // small (see list-notes.ts / list-shared.ts ProjectionExpression).
        // A list response must NEVER overwrite a cached document body — doing
        // so wipes the Y.Doc snapshot mid-session, forces useNoteCollab to
        // tear down and recreate the session, and orphans the editor on a
        // destroyed Y.Doc. Always carry forward whichever side actually has
        // the body fields.
        const mergeOne = (
          server: Note,
          local: Note | undefined,
          staleSink: string[]
        ): Note => {
          if (!local) return server;
          const serverTime = new Date(server.updatedAt).getTime();
          const localTime = new Date(local.updatedAt).getTime();
          const fresher =
            Number.isFinite(localTime) && localTime > serverTime ? local : server;
          // Server side is newer than what we cached — list endpoint
          // stripped content + yjsState, so the merged note ends up with
          // server's new updatedAt but our stale body. Flag for backfill
          // so refreshNote(id) pulls the canonical content via get-note.
          if (Number.isFinite(serverTime) && serverTime > localTime) {
            staleSink.push(server.id);
          }
          return {
            ...fresher,
            content: server.content ?? local.content ?? null,
            yjsState: server.yjsState ?? local.yjsState,
            // `isPage` is immutable after creation and is owner-authoritative —
            // never an in-flight local edit. Always trust the server so a stale
            // cache can't keep showing an in-note page in the workspace sidebar.
            // (parentId / pageOrder / color stay under LWW since the user edits
            // those optimistically via move / reorder / recolor.)
            isPage: server.isPage,
          };
        };
        const ownedLocal = new Map(s.notes.map((n) => [n.id, n]));
        const sharedLocal = new Map(s.sharedNotes.map((n) => [n.id, n]));
        const mergedOwned = serverNotes.map((server) =>
          mergeOne(server, ownedLocal.get(server.id), staleOwnedIds)
        );
        const mergedShared = serverShared.map((server) =>
          mergeOne(server, sharedLocal.get(server.id), staleSharedIds)
        );

        // CRITICAL: the list responses are NOT the complete set of records the
        // user can see. In-note PAGES are fetched out-of-band via get-note —
        // an editor's pages live in the OWNER's partition (absent from both
        // list-notes and list-shared), and a just-created page may not be
        // server-indexed yet. A naive `notes = serverList` therefore drops
        // them and the rail goes stale. Carry forward any local record the
        // server list omits when it's a page, or an optimistic create still
        // inside a grace window.
        const RECENT_MS = 60_000;
        const isCarryForward = (n: Note): boolean => {
          if (n.isPage) return true;
          const created = new Date(n.createdAt).getTime();
          return Number.isFinite(created) && Date.now() - created < RECENT_MS;
        };
        const serverOwnedIds = new Set(serverNotes.map((n) => n.id));
        const serverSharedIds = new Set(serverShared.map((n) => n.id));
        const preservedOwned = s.notes.filter(
          (n) => !serverOwnedIds.has(n.id) && isCarryForward(n)
        );
        const preservedShared = s.sharedNotes.filter(
          (n) => !serverSharedIds.has(n.id) && isCarryForward(n)
        );

        return {
          notes: [...mergedOwned, ...preservedOwned],
          sharedNotes: [...mergedShared, ...preservedShared],
          isLoading: false,
        };
      });

      // Backfill stale entries with full content + yjsState via get-note.
      // Fire and forget — refreshNote handles its own error logging and
      // the merged metadata is already on screen. Slight stagger keeps a
      // burst of refreshes from saturating the API on cold load.
      const refreshNoteAction = get().refreshNote;
      const staleAll = Array.from(new Set([...staleOwnedIds, ...staleSharedIds]));
      for (let i = 0; i < staleAll.length; i++) {
        const id = staleAll[i];
        setTimeout(() => {
          void refreshNoteAction(id);
        }, i * 25);
      }
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : "Failed to fetch notes",
      });
    }
  },

  fetchCommentsForNote: async (noteId) => {
    if (DEV_AUTH_BYPASS) return;
    try {
      const response = await apiListNoteComments(noteId);
      const fresh = response.items.map(mapCommentResponse);
      set((s) => {
        // Replace only this note's comments. Other notes' cached comments
        // stay in place so the user doesn't see them flicker out of comment
        // counts / inline highlights while panels for other notes are still
        // mounted (rare, but cheap to preserve).
        const others = s.comments.filter((c) => c.noteId !== noteId);
        return { comments: [...others, ...fresh] };
      });
    } catch (err) {
      console.warn("[notes] fetchCommentsForNote failed:", err);
    }
  },

  fetchSharedNotes: async () => {
    if (DEV_AUTH_BYPASS) return;
    try {
      const response = await apiListSharedNotes();
      const fresh = response.items.map(mapNoteResponse);
      set((s) => {
        // Preserve in-note pages (fetched via get-note, never in this list) and
        // recent optimistic creates so a shared-list refresh can't drop them.
        const freshIds = new Set(fresh.map((n) => n.id));
        const preserved = s.sharedNotes.filter(
          (n) =>
            !freshIds.has(n.id) &&
            (n.isPage ||
              Date.now() - new Date(n.createdAt).getTime() < 60_000)
        );
        return { sharedNotes: [...fresh, ...preserved] };
      });
    } catch (err) {
      console.warn("[notes] failed to fetch shared notes:", err);
    }
  },

  refreshNote: async (id, pageParentId) => {
    // Stale-while-revalidate: cached note is already on screen; refresh from
    // server in the background so a refresher sees a co-collaborator's
    // latest committed state without waiting on the full list fetch.
    if (DEV_AUTH_BYPASS) return;
    try {
      const fresh = mapNoteResponse(
        await apiGetNote(id, pageParentId ? { parentId: pageParentId } : undefined)
      );
      set((s) => {
        // Last-write-wins: keep local edits if they're newer than the
        // server's snapshot — autosave will flush them out shortly.
        const preferLocal = (local: Note): boolean => {
          const serverTime = new Date(fresh.updatedAt).getTime();
          const localTime = new Date(local.updatedAt).getTime();
          return Number.isFinite(localTime) && localTime > serverTime;
        };
        const inOwned = s.notes.find((n) => n.id === id);
        if (inOwned) {
          if (preferLocal(inOwned)) return s;
          return {
            notes: normalizeAllOrders(
              s.notes.map((n) => (n.id === id ? { ...n, ...fresh } : n))
            ),
          };
        }
        const inShared = s.sharedNotes.find((n) => n.id === id);
        if (inShared) {
          // Shared notes: trust the server unconditionally. preferLocal
          // protects owner in-flight typing, but shared-note recipients
          // can't have edits ahead of server in steady state (autosave
          // PUTs bump server). Any concurrent edit by the recipient is
          // covered by Yjs CRDT merge inside the live collab session —
          // applying server's snapshot doesn't drop those edits, the
          // CRDT will reconcile both branches on the next update tick.
          return {
            sharedNotes: s.sharedNotes.map((n) =>
              n.id === id ? { ...n, ...fresh } : n
            ),
          };
        }
        // Note wasn't in either list yet (cold link) — drop it into shared if
        // we got an explicit role, otherwise owned.
        if (fresh.myRole && fresh.myRole !== "owner") {
          return { sharedNotes: [...s.sharedNotes, fresh] };
        }
        return { notes: normalizeAllOrders([...s.notes, fresh]) };
      });
    } catch (err) {
      // 404 is fine (note was deleted). Other errors logged for diagnosis.
      const status = (err as { status?: number })?.status;
      if (status !== 404) {
        console.warn("[notes] refreshNote failed:", err);
      }
    }
  },

  shareNoteWith: async (noteId, userId, role) => {
    const response = await apiShareNote(noteId, { userId, role });
    set((s) => ({
      notes: s.notes.map((n) =>
        n.id === noteId ? { ...n, sharedWith: response.sharedWith } : n
      ),
    }));
  },

  unshareNoteWith: async (noteId, userId) => {
    const response = await apiUnshareNote(noteId, userId);
    set((s) => ({
      notes: s.notes.map((n) =>
        n.id === noteId ? { ...n, sharedWith: response.sharedWith } : n
      ),
    }));
  },

  leaveShared: async (noteId) => {
    await apiLeaveSharedNote(noteId);
    set((s) => ({
      sharedNotes: s.sharedNotes.filter((n) => n.id !== noteId),
    }));
  },

   createNoteRemote: async (data) => {
     set({ error: null });

     // In dev bypass mode, create locally only
      if (DEV_AUTH_BYPASS) {
        const siblingCount = get().notes.filter(
          (note) =>
            (note.parentId ?? null) === (data.parentId ?? null) && !note.isDeleted
        ).length;
        const note: Note = {
          id: generateUUID(),
         title: data.title ?? "",
         content: null,
         parentId: data.parentId ?? null,
         order: siblingCount,
         tags: [],
         isFavorite: false,
         isDeleted: false,
         createdAt: new Date().toISOString(),
         updatedAt: new Date().toISOString(),
         createdBy: "dev-user",
       };
       set((s) => ({ notes: normalizeAllOrders([note, ...s.notes]) }));
       return note.id;
     }

     // Pre-generate the id client-side (the API honours `body.id`) so the new
     // note can be inserted into the sidebar *immediately* — no blank wait
     // while the POST round-trips. We still await the POST before resolving so
     // chained writes (template/import content seeding) can't race a 404.
     const id = generateUUID();
     const siblingCount = get().notes.filter(
       (entry) =>
         (entry.parentId ?? null) === (data.parentId ?? null) && !entry.isDeleted
     ).length;
     const now = new Date().toISOString();
     const optimistic: Note = {
       id,
       title: data.title ?? "",
       content: (data.content as Note["content"]) ?? null,
       parentId: data.parentId ?? null,
       order: siblingCount,
       tags: [],
       isFavorite: false,
       isDeleted: false,
       createdAt: now,
       updatedAt: now,
       createdBy: currentProfileId() ?? "",
     };
     set((s) => ({ notes: normalizeAllOrders([optimistic, ...s.notes]) }));

     try {
       const response = await apiCreateNote({ ...data, id, order: siblingCount });
       const note = { ...mapNoteResponse(response), order: response.order ?? siblingCount };
       // Reconcile the optimistic row with the server's canonical record.
       set((s) => ({
         notes: normalizeAllOrders(s.notes.map((n) => (n.id === id ? note : n))),
       }));
       return note.id;
     } catch (err) {
       // Roll back the optimistic insert on failure.
       set((s) => ({
         notes: s.notes.filter((n) => n.id !== id),
         error: err instanceof Error ? err.message : "Failed to create note",
       }));
       throw err;
     }
  },

  updateNoteRemote: async (id, updates, pageParentId) => {
    // Optimistic update — applies to whichever list (owned or shared) the note lives in.
    const prevNotes = get().notes;
    const prevShared = get().sharedNotes;
    const stamp = new Date().toISOString();
    const inOwned = prevNotes.some((n) => n.id === id);

    if (inOwned) {
      set((s) => ({
        notes: normalizeAllOrders(
          s.notes.map((n) =>
            n.id === id ? { ...n, ...updates, updatedAt: stamp } : n
          )
        ),
        error: null,
      }));
    } else {
      set((s) => ({
        sharedNotes: s.sharedNotes.map((n) =>
          n.id === id ? { ...n, ...updates, updatedAt: stamp } : n
        ),
        error: null,
      }));
    }

    // In dev bypass mode, local update is sufficient
    if (DEV_AUTH_BYPASS) return;

    try {
      await apiUpdateNote(
        id,
        updates,
        pageParentId ? { parentId: pageParentId } : undefined
      );
    } catch (err) {
      // Rollback on failure
      set({
        notes: prevNotes,
        sharedNotes: prevShared,
        error:
          err instanceof Error ? err.message : "Failed to update note",
      });
      throw err;
    }
  },

  // --- In-note pages ---

  createPage: async (parentNoteId, title) => {
    const id = generateUUID();
    const existing = get().getPagesForNote(parentNoteId);
    const order = existing.length;
    const now = new Date().toISOString();
    // Is the current user the OWNER of the parent (parent in `notes`) or an
    // editor of a note shared with them (parent in `sharedNotes`)? Pages always
    // belong to the owner's partition server-side; the editor's local copy
    // lives in `sharedNotes` so a later list refresh (which won't return the
    // owner's page) can't drop it.
    const ownsParent = get().notes.some((n) => n.id === parentNoteId);
    const parent =
      get().notes.find((n) => n.id === parentNoteId) ??
      get().sharedNotes.find((n) => n.id === parentNoteId);
    const nextPageOrder = [...(parent?.pageOrder ?? []), id];

    const page: Note = {
      id,
      title: title ?? `Page ${order + 2}`,
      content: null,
      parentId: parentNoteId,
      order,
      tags: [],
      isFavorite: false,
      isDeleted: false,
      createdAt: now,
      updatedAt: now,
      createdBy: parent?.ownerId ?? useUserStore.getState().user?.id ?? "",
      isPage: true,
      ...(ownsParent ? {} : { ownerId: parent?.ownerId, myRole: "edit" as const }),
    };

    // Optimistic insert into the right list + local parent pageOrder bump.
    // The backend (create-note) owns the canonical pageOrder append, so we do
    // NOT PUT pageOrder here (an editor can't write owner-only fields anyway).
    set((s) => ({
      notes: [
        ...(ownsParent ? [page] : []),
        ...s.notes.map((n) =>
          n.id === parentNoteId ? { ...n, pageOrder: nextPageOrder } : n
        ),
      ],
      sharedNotes: [
        ...(ownsParent ? [] : [page]),
        ...s.sharedNotes.map((n) =>
          n.id === parentNoteId ? { ...n, pageOrder: nextPageOrder } : n
        ),
      ],
    }));

    if (!DEV_AUTH_BYPASS) {
      try {
        await apiCreateNote({
          id,
          title: page.title,
          parentId: parentNoteId,
          order,
          isPage: true,
        });
      } catch (err) {
        set((s) => ({
          notes: s.notes.filter((n) => n.id !== id),
          sharedNotes: s.sharedNotes.filter((n) => n.id !== id),
          error: err instanceof Error ? err.message : "Failed to create page",
        }));
      }
    }
    return id;
  },

  deletePage: async (pageId) => {
    const page =
      get().notes.find((n) => n.id === pageId) ??
      get().sharedNotes.find((n) => n.id === pageId);
    if (!page || !page.parentId) return;
    const parentId = page.parentId;
    const parent =
      get().notes.find((n) => n.id === parentId) ??
      get().sharedNotes.find((n) => n.id === parentId);
    const ownsParent = get().notes.some((n) => n.id === parentId);
    const nextPageOrder = (parent?.pageOrder ?? []).filter((p) => p !== pageId);

    set((s) => ({
      notes: s.notes
        .filter((n) => !(n.id === pageId))
        .map((n) => (n.id === parentId ? { ...n, pageOrder: nextPageOrder } : n)),
      sharedNotes: s.sharedNotes
        .filter((n) => !(n.id === pageId))
        .map((n) => (n.id === parentId ? { ...n, pageOrder: nextPageOrder } : n)),
    }));

    if (!DEV_AUTH_BYPASS) {
      // pageOrder is editable by edit-role; the page delete carries the parent
      // hint so an editor can delete a page that lives in the owner's partition.
      void apiUpdateNote(parentId, { pageOrder: nextPageOrder }).catch(() => {});
      void apiDeleteNote(pageId, ownsParent ? undefined : { parentId }).catch((err) => {
        set({ error: err instanceof Error ? err.message : "Failed to delete page" });
      });
    }
  },

  reorderPages: (parentNoteId, orderedPageIds) => {
    set((s) => ({
      notes: s.notes.map((n) =>
        n.id === parentNoteId ? { ...n, pageOrder: orderedPageIds } : n
      ),
      sharedNotes: s.sharedNotes.map((n) =>
        n.id === parentNoteId ? { ...n, pageOrder: orderedPageIds } : n
      ),
    }));
    if (!DEV_AUTH_BYPASS) {
      void apiUpdateNote(parentNoteId, { pageOrder: orderedPageIds }).catch((err) => {
        set({ error: err instanceof Error ? err.message : "Failed to reorder pages" });
      });
    }
  },

  deleteNoteRemote: async (id) => {
    const prevNotes = get().notes;
    // Optimistic update
    set((s) => ({
      notes: normalizeAllOrders(
        s.notes.map((n) =>
          n.id === id ? { ...n, isDeleted: true } : n
        )
      ),
      error: null,
    }));

    // In dev bypass mode, local update is sufficient
    if (DEV_AUTH_BYPASS) return;

    try {
      await apiDeleteNote(id);
    } catch (err) {
      // Rollback on failure
      set({
        notes: prevNotes,
        error:
          err instanceof Error ? err.message : "Failed to delete note",
      });
      throw err;
    }
  },

  // --- Computed ---

  getActiveNote: () => {
    const { notes, sharedNotes, activeNoteId } = get();
    if (!activeNoteId) return undefined;
    return (
      notes.find((n) => n.id === activeNoteId) ??
      sharedNotes.find((n) => n.id === activeNoteId)
    );
  },

  getFilteredNotes: () => {
    const { notes, searchQuery, selectedTags, sortField, sortDirection } =
      get();
    // In-note pages never surface in the top-level workspace list.
    let filtered = notes.filter((n) => !n.isDeleted && !n.isPage);

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((n) => {
        if (n.title.toLowerCase().includes(q)) return true;
        if (n.tags.some((tag) => tag.toLowerCase().includes(q))) return true;
        return false;
      });
    }

    if (selectedTags.length > 0) {
      filtered = filtered.filter((n) =>
        selectedTags.some((tag) => n.tags.includes(tag))
      );
    }

    filtered.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return sortDirection === "asc" ? cmp : -cmp;
    });

    return filtered;
  },

  getRootNotes: () =>
    get()
      .notes.filter((n) => !n.parentId && !n.isDeleted && !n.isPage)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),

  getChildNotes: (parentId) =>
    get()
      .notes.filter((n) => n.parentId === parentId && !n.isDeleted && !n.isPage)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),

  /** In-note pages belonging to a note, ordered for the page rail. Searches
   *  both owned + shared lists (a recipient's pages land in sharedNotes). */
  getPagesForNote: (noteId) => {
    const { notes, sharedNotes } = get();
    const seen = new Set<string>();
    return [...notes, ...sharedNotes]
      .filter((n) => {
        if (n.parentId !== noteId || !n.isPage || n.isDeleted) return false;
        if (seen.has(n.id)) return false;
        seen.add(n.id);
        return true;
      })
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id.localeCompare(b.id));
  },

  getFavoriteNotes: () => {
    const { notes, sharedNotes } = get();
    return [
      ...notes.filter((n) => n.isFavorite && !n.isDeleted),
      ...sharedNotes.filter((n) => n.isFavorite && !n.isDeleted),
    ];
  },

  getDeletedNotes: () => get().notes.filter((n) => n.isDeleted),

  resetForProfile: (nextProfileId) => {
    if (notesPersistTimer) {
      clearTimeout(notesPersistTimer);
      notesPersistTimer = null;
    }
    set({
      notes: loadFromStorage<Note[]>(STORAGE_PREFIXES.NOTES, nextProfileId, []),
      sharedNotes: loadFromStorage<Note[]>(
        STORAGE_PREFIXES.SHARED_NOTES,
        nextProfileId,
        []
      ),
      comments: DEV_AUTH_BYPASS ? loadCommentsFromStorage(nextProfileId) : [],
      activeNoteId: null,
      activeCommentThreadId: null,
      searchQuery: "",
      selectedTags: [],
      error: null,
      isLoading: false,
    });
  },
}));

// Persist notes + shared notes to localStorage whenever they change so reloads
// can render them synchronously before the network catches up.
useNotesStore.subscribe((state, prev) => {
  if (state.notes === prev.notes && state.sharedNotes === prev.sharedNotes) {
    return;
  }
  scheduleNotesPersist(state.notes, state.sharedNotes);
});

// Profile (persona) switch: wipe in-memory state + re-hydrate from the new
// profile's scoped storage. Without this the old persona's notes briefly
// remain on screen after the switch — a security + privacy bug. Mirrors
// the messenger-store subscriber pattern.
if (typeof window !== "undefined") {
  let prevProfileId = useProfileSwitcherStore.getState().activeProfileId;
  useProfileSwitcherStore.subscribe((state) => {
    const nextProfileId = state.activeProfileId;
    if (nextProfileId === prevProfileId) return;
    prevProfileId = nextProfileId;
    migrateLegacyKey(STORAGE_PREFIXES.NOTES, nextProfileId);
    migrateLegacyKey(STORAGE_PREFIXES.SHARED_NOTES, nextProfileId);
    migrateLegacyKey(STORAGE_PREFIXES.COMMENTS, nextProfileId);
    useNotesStore.getState().resetForProfile(nextProfileId);
    if (!DEV_AUTH_BYPASS && nextProfileId) {
      void useNotesStore.getState().fetchNotes();
    }
  });
}

// Logout: wipe the active profile's notes from memory + storage so the next
// account signing in on this device never sees them. Other profiles'
// scoped entries remain — each is isolated by its own key. Mirrors the
// messenger-store logout subscriber.
if (typeof window !== "undefined") {
  let wasAuthed = useUserStore.getState().isAuthenticated;
  useUserStore.subscribe((state) => {
    const isAuthed = state.isAuthenticated;
    if (wasAuthed && !isAuthed) {
      const activeProfile = useProfileSwitcherStore.getState().activeProfileId;
      clearScopedStorage(activeProfile);
      useNotesStore.getState().resetForProfile(null);
    }
    wasAuthed = isAuthed;
  });
}
